import type { ReactNode } from 'react';
import type { AvisoDeProgreso, FaseDeProgreso } from '../../../common/services/api.js';
import { diccionarioDe, type Idioma } from '../../../i18n/index.js';

/**
 * Lo que se ve mientras Athena responde.
 *
 * STREAM-01 (2026-09-29): los avisos llegan del propio motor por SSE (`ResponderPreguntaService`,
 * evento `progreso`), fase a fase según ocurre: «Buscando en N estudios», «Leyendo N estudios»,
 * «Redactando la respuesta», «Comprobando cada cifra». Nada de tiempos ni porcentajes: solo se
 * enseñan las fases alcanzadas, las terminadas con su marca y la actual con su viñeta.
 *
 * Viñetas (encargo de Iván, estética de la marca: teal como acento y oro para lo que es prueba):
 * - buscando: una nube de puntos en miniatura, como el mapa, con puntos que se encienden;
 * - leyendo: fichas de estudio que llegan y se apilan (sin nombres: el evento no los trae y no se
 *   hace ninguna llamada más para pedirlos);
 * - redactando: líneas de texto que se dibujan;
 * - comprobando: una columna de marcas que se encienden en oro, porque es la verificación.
 * Todo es SVG con animaciones CSS, sin JavaScript por fotograma. Con «reducir movimiento», cada
 * viñeta se queda en un fotograma quieto. Las viñetas son decorado (`aria-hidden`); lo que se
 * anuncia es el texto de la fase, una vez por cambio.
 *
 * Sin avisos todavía (el primero aún no ha llegado, o no hay SSE y `preguntar()` cayó al `fetch` de
 * siempre) se ve la nube con el texto neutro de siempre.
 */
export function Esperando({ idioma, avisos }: { readonly idioma: Idioma; readonly avisos: readonly AvisoDeProgreso[] }) {
  const t = diccionarioDe(idioma).consulta.esperando;
  const actual = avisos.at(-1);
  const hechas = avisos.slice(0, -1);
  const fase = actual?.fase ?? 'buscando';

  return (
    <div className='progreso flex items-start gap-5'>
      <div aria-hidden='true' className='progreso-vineta shrink-0'>
        {/* La clave hace que cada fase entre con su fundido al cambiar. */}
        <div key={fase} className='progreso-entra'>
          {VINETAS[fase]}
        </div>
      </div>
      <ol className='m-0 flex list-none flex-col gap-1 p-0 pt-1 font-(family-name:--text) text-[15px]'>
        {hechas.map(a => (
          <li key={`${a.fase ?? 'sin'}-${a.texto}`} className='flex items-center gap-2 text-(--ink-faint)'>
            <Marca />
            {a.texto}
          </li>
        ))}
        <li className='flex items-center gap-2 text-(--ink-soft)'>
          <span aria-hidden='true' className='progreso-punto inline-block h-[7px] w-[7px] rounded-full bg-(--teal-deep)' />
          {actual?.texto ?? t.buscando}
        </li>
      </ol>
      {/* Lo que se anuncia: un nodo fijo que solo cambia de texto al cambiar de fase. */}
      <p aria-live='polite' className='sr-only'>
        {actual?.texto ?? t.buscando}
      </p>
    </div>
  );
}

function Marca() {
  return (
    <svg aria-hidden='true' width='12' height='12' viewBox='0 0 12 12' className='shrink-0'>
      <path d='M2 6.5 L5 9 L10 3' fill='none' stroke='var(--teal)' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );
}

/** Una nube fija de puntos, siempre la misma (un generador con semilla), como una miniatura del mapa. */
const PUNTOS = (() => {
  let s = 11;
  const r = () => (s = (s * 16807) % 2147483647) / 2147483647;
  const centros = [
    [22, 22],
    [58, 16],
    [86, 34],
    [44, 44],
    [70, 52],
  ] as const;
  return Array.from({ length: 46 }, (_, i) => {
    const [cx, cy] = centros[i % centros.length] ?? [54, 32];
    return { x: cx + (r() - 0.5) * 22, y: cy + (r() - 0.5) * 16, retraso: (i * 0.37) % 2.4, encendido: i % 4 === 0 };
  });
})();

const VINETAS: Readonly<Record<FaseDeProgreso, ReactNode>> = {
  buscando: (
    <svg width='108' height='64' viewBox='0 0 108 64'>
      {PUNTOS.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={p.encendido ? 2 : 1.4}
          className={p.encendido ? 'progreso-consultado' : 'progreso-nube'}
          style={p.encendido ? { animationDelay: `${String(p.retraso)}s` } : undefined}
        />
      ))}
    </svg>
  ),
  leyendo: (
    <svg width='108' height='64' viewBox='0 0 108 64'>
      {[0, 1, 2, 3].map(k => (
        <g key={k} className='progreso-ficha' style={{ animationDelay: `${String(k * 0.55)}s` }}>
          <rect x={30 + k * 6} y={10 + k * 4} width='40' height='30' rx='2' className='progreso-ficha-papel' />
          <rect x={35 + k * 6} y={16 + k * 4} width='22' height='2.4' rx='1.2' className='progreso-ficha-linea' />
          <rect x={35 + k * 6} y={22 + k * 4} width='28' height='2' rx='1' className='progreso-ficha-linea-tenue' />
          <rect x={35 + k * 6} y={27 + k * 4} width='18' height='2' rx='1' className='progreso-ficha-linea-tenue' />
        </g>
      ))}
    </svg>
  ),
  redactando: (
    <svg width='108' height='64' viewBox='0 0 108 64'>
      {[70, 84, 56, 76].map((ancho, k) => (
        <rect
          key={k}
          x='14'
          y={12 + k * 11}
          width={ancho}
          height='3'
          rx='1.5'
          className='progreso-linea'
          style={{ animationDelay: `${String(k * 0.45)}s` }}
        />
      ))}
    </svg>
  ),
  comprobando: (
    <svg width='108' height='64' viewBox='0 0 108 64'>
      {[0, 1, 2, 3].map(k => (
        <g key={k}>
          <rect x='40' y={9 + k * 12} width='40' height='2.6' rx='1.3' className='progreso-ficha-linea-tenue' />
          <path d={`M26 ${String(10 + k * 12)} l3 3 l6 -6.5`} className='progreso-cheque' style={{ animationDelay: `${String(k * 0.5)}s` }} />
        </g>
      ))}
    </svg>
  ),
};
